import { RenderDevice } from '../RenderDevice';
import { RenderDeviceWebGL } from '../RenderDeviceWebGL';
import {
  AssetManager,
  AtlasAttachmentLoader,
  ManagedWebGLRenderingContext,
  SkeletonBinary,
  SkeletonData,
  SkeletonJson,
} from '@esotericsoftware/spine-webgl';

export interface SpineAssetBundle {
  skeletonData: SkeletonData;
  isPma: boolean;
}

export interface SpineAssetLoadOptions {
  assetPath: string;
  atlasName: string;
  /** .json или .skel — формат определяется автоматически по расширению */
  skeletonName: string;
}

export class SpineAssetCache {
  private static _cache: Map<string, SpineAssetBundle> = new Map();

  static async load(renderDevice: RenderDevice, options: SpineAssetLoadOptions): Promise<SpineAssetBundle> {
    const key = `${options.assetPath}|${options.atlasName}|${options.skeletonName}`;

    if (this._cache.has(key)) {
      return this._cache.get(key)!;
    }

    const gl = (renderDevice as RenderDeviceWebGL).getGLContext();
    const isPma = await this._detectAtlasPma(options.assetPath, options.atlasName);

    const spineContext = new ManagedWebGLRenderingContext(gl);

    if (!isPma) {
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    }

    const isBinary = options.skeletonName.endsWith('.skel');
    const assetManager = new AssetManager(spineContext, options.assetPath);
    assetManager.loadTextureAtlas(options.atlasName);
    if (isBinary) {
      assetManager.loadBinary(options.skeletonName);
    } else {
      assetManager.loadJson(options.skeletonName);
    }
    await assetManager.loadAll();

    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    (renderDevice as RenderDeviceWebGL).invalidateGLCache();

    const atlas = assetManager.get(options.atlasName);
    const atlasLoader = new AtlasAttachmentLoader(atlas);
    const skeletonData = isBinary
      ? new SkeletonBinary(atlasLoader).readSkeletonData(assetManager.get(options.skeletonName))
      : new SkeletonJson(atlasLoader).readSkeletonData(assetManager.get(options.skeletonName));

    const bundle: SpineAssetBundle = { skeletonData, isPma };
    this._cache.set(key, bundle);
    return bundle;
  }

  static clear(): void {
    this._cache.clear();
  }

  private static async _detectAtlasPma(assetPath: string, atlasName: string): Promise<boolean> {
    try {
      const response = await fetch(assetPath + atlasName);
      const text = await response.text();
      return text.includes('pma: true');
    } catch {
      return false;
    }
  }
}
